import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '@/shared/api/client'
import { useApiClient } from '@/shared/api/use-api-client'

/** One record as a details or edit page loads it. */
export type RecordState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'missing' }
  | { readonly status: 'error' }
  | {
      readonly status: 'ready'
      readonly record: T
      /** Sent back as `ifMatch` when the record is saved. */
      readonly version: string | null
    }

/**
 * Loads one record and its version from `path`, again on `reload`. A 404 or 403 is `missing`: the
 * record does not exist, or the caller may not see it, which trying again cannot change. `parse`
 * narrows the body and throws on anything else, which shows as an error; keep it at module level,
 * as a new one loads again.
 * `isKeptOnReload` keeps the record shown while `reload` reads it again, so a page whose save reloads it
 * is not unmounted, taking the focused button with it (WCAG 2.4.3).
 */
export function useRecord<T>(
  path: string,
  parse: (body: unknown) => T,
  options?: { readonly isKeptOnReload?: boolean },
): RecordState<T> & { readonly reload: () => void } {
  const client = useApiClient()
  const [attempt, setAttempt] = useState(0)
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly path: string; readonly state: RecordState<T> } | null>(null)
  const key = `${path}\u0000${String(attempt)}`

  useEffect(() => {
    const controller = new AbortController()
    const load = async () => {
      try {
        const result = await client.getRecord(path, controller.signal)
        setLoaded({ key, path, state: { status: 'ready', record: parse(result.data), version: result.version } })
      } catch (error: unknown) {
        if (!controller.signal.aborted) {
          const isHidden = error instanceof ApiError && (error.status === 404 || error.status === 403)
          setLoaded({ key, path, state: { status: isHidden ? 'missing' : 'error' } })
        }
      }
    }
    void load()
    return () => controller.abort()
  }, [client, path, parse, key])

  const reload = useCallback(() => setAttempt((current) => current + 1), [])
  const isKept = options?.isKeptOnReload === true && loaded?.path === path && loaded.state.status === 'ready'
  const state: RecordState<T> = loaded !== null && (loaded.key === key || isKept) ? loaded.state : { status: 'loading' }
  return { ...state, reload }
}
